import "./sidebar.css";
import { useContext } from "react";
import { DataContext } from "src/contexts/DataContext";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { Option, Select } from "../select";
import { MenuContext } from "src/contexts/MenuContext";
import {
  EDFDataContextType,
  useChannelData,
  useEDFDataParams,
} from "src/contexts/EDFDataContext";

const TITLE_TEXT = "VISUALIZING LIFE IN THE DEEP";
const ALLOWED_VALUES = ["Sleep_Code", "Simple_Sleep_Code"];

function Header() {
  return (
    <div id="sleep-at-sea-header">
      <img
        className="logo"
        src={"images/SleepAtSea.png"}
        alt="Life in the Deep Logo"
      />
      <div className="title">{TITLE_TEXT}</div>
    </div>
  );
}

function Toggle({ label }) {
  const { selectedVar, setSelectedVar } = useContext(DataContext);

  function handleChange() {
    setSelectedVar(label);
  }

  return (
    <div className="toggle-data-attribs">
      <FormControlLabel
        control={
          <Switch checked={label === selectedVar} onChange={handleChange} />
        }
        label={label}
      />
    </div>
  );
}

function DataPanel() {
  const { data } = useContext(DataContext);

  if (data.length === 0) return <></>;
  const data_sample = data[0];

  return (
    <div className="data-panel">
      {Object.keys(data_sample)
        .filter(
          (d) =>
            typeof data_sample[d] === "string" ||
            data_sample[d] instanceof String
        )
        .filter((d) => ALLOWED_VALUES.includes(d))
        .map((d) => (
          <Toggle key={d} label={d} />
        ))}
    </div>
  );
}

type EDFParamConfig = {
  label: string;
  type: "date" | "number" | "string";
  formatter: (value: any) => string;
};

const EDFParamsMap: Record<keyof EDFDataContextType, EDFParamConfig> = {
  startDate: {
    label: "Start",
    type: "date",
    formatter: (value: number) => `${new Date(value).toLocaleString()}`,
  },
  endDate: {
    label: "End",
    type: "date",
    formatter: (value: number) => `${new Date(value).toLocaleString()}`,
  },
  freq: {
    label: "Frequency",
    type: "number",
    formatter: (value: number) => `${value} Hz`,
  },
  limit: {
    label: "Limit",
    type: "number",
    formatter: (value: number) =>
      `${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
  },
  lFreq: {
    label: "Low Pass Filter",
    type: "number",
    formatter: (value: number) => (value ? `${value} Hz` : "none"),
  },
  hFreq: {
    label: "High Pass Filter",
    type: "number",
    formatter: (value: number) => (value ? `${value} Hz` : "none"),
  },
};

const ChannelPanel = () => {
  const { channels, selectedChannel, setSelectedChannel } =
    useContext(DataContext);
  const { data, isLoading } = useChannelData();

  const params = useEDFDataParams();

  const onSelect = (e, value) => {
    setSelectedChannel(value);
  };
  return (
    <>
      <h3>Displayed Channel</h3>
      <Select defaultValue={null} onChange={onSelect}>
        <Option value={null}>No selected channel</Option>
        {channels?.map((channel) => (
          <Option key={channel} value={channel}>
            {channel}
          </Option>
        ))}
      </Select>
      {!!selectedChannel && (
        <div className="edf-params">
          <h3>Channel Parameters</h3>
          <p><b>Data Status:</b> {isLoading ? "Loading..." : "Loaded"}</p>
          {data?.data.length > 0 &&
            Object.entries(EDFParamsMap).map(([key, config]) => params[key] && (
              <p key={key}>
                <b>{config.label}</b>: {config.formatter(params[key])}
              </p>
            ))}
        </div>
      )}
    </>
  );
};

export default function Sidebar() {
  const { isOpen } = useContext(MenuContext);

  return (
    <div className={`sidebar ${isOpen ? "open" : "closed"}`}>
      <Header />
      <h3>Categorical Variables</h3>
      <DataPanel />
      <ChannelPanel />
    </div>
  );
}
